import _ from 'lodash'
import type {SerializedCompStructure, StyleRef} from '@wix/document-services-types'
import type {
    ParsedStyleItem,
    PostStyleMigrationCallback,
    SelectorsMapping,
    StyleItemWithMetadata,
    StyleItemWithStyleProperties
} from './types'
import {
    createPropertiesSource,
    mergeStylesByBreakpoints,
    migratedStyleToStyleProperties,
    migrateProperties,
    parseStylableStyleItems
} from './index'
import {isStyleRef} from '../migratorUtils'

interface MigrateStylesProps {
    parsedStyleItems: ParsedStyleItem[]
    newSkin: string
    useCssColor: boolean
    withBorderRadiusAsOneParam: boolean
    postStyleMigrationCallback?: PostStyleMigrationCallback
}

interface MigrateStylesReturnObject {
    styleItem: StyleRef
    mobileStyleProperties?: StyleItemWithStyleProperties['style']
    scopedStyleId?: string
}
export interface MigrateStylableComponentProps {
    serializedComponent: SerializedCompStructure
    newSkin: string
    selectorsMappings: SelectorsMapping
    useCssColor?: boolean
    withBorderRadiusAsOneParam?: boolean
    postStyleMigrationCallback?: PostStyleMigrationCallback
}

const MOBILE_VARIANT = 'MOBILE-VARIANT'

const getStyleWithMetadata = (styleRef: StyleRef, range: {max?: number; min?: number} = {}): StyleItemWithMetadata => {
    const {max, min} = range
    const type: StyleItemWithMetadata['metadata']['type'] =
        max !== undefined && min !== undefined ? 'breakpoint' : 'default'
    return {
        metadata: {type, max, min, isMobileOnly: false},
        style: styleRef.style
    } as StyleItemWithMetadata
}

const migrateStyles = ({
    parsedStyleItems,
    newSkin,
    useCssColor,
    withBorderRadiusAsOneParam,
    postStyleMigrationCallback
}: MigrateStylesProps): MigrateStylesReturnObject[] => {
    const migratedStyleItems = _.flow([
        mergeStylesByBreakpoints,
        styleItems => migrateProperties({useCssColor, withBorderRadiusAsOneParam})(styleItems),
        createPropertiesSource
    ])(parsedStyleItems) as ParsedStyleItem[]

    return migratedStyleItems.map(migratedStyleItem => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {parsedStyle, metadata, scopedStyleId, ...styleItem} = migratedStyleItem
        let mobileStyleProperties: StyleItemWithStyleProperties['style'] | undefined

        if (Object.values(parsedStyle).some(style => style.mobileProperties.length)) {
            mobileStyleProperties = migratedStyleToStyleProperties(migratedStyleItem, {
                postStyleMigrationCallback,
                isMobile: true
            })
        }

        return {
            scopedStyleId,
            styleItem: {
                ...(styleItem as StyleRef),
                skin: newSkin,
                style: migratedStyleToStyleProperties(migratedStyleItem, {
                    postStyleMigrationCallback
                })
            },
            mobileStyleProperties
        }
    })
}

const migrateStylableComponent = ({
    serializedComponent,
    newSkin,
    selectorsMappings,
    useCssColor = false,
    withBorderRadiusAsOneParam = false,
    postStyleMigrationCallback
}: MigrateStylableComponentProps) => {
    if (isStyleRef(serializedComponent.style)) {
        const stylesWithMetadata: StyleItemWithMetadata[] = [getStyleWithMetadata(serializedComponent.style)]
        const {scopedStyles, referredVariants} = serializedComponent

        if (scopedStyles && referredVariants) {
            const scopedStylesWithMetadata = Object.entries(scopedStyles).reduce<StyleItemWithMetadata[]>(
                (acc, [scopedStyleId, scopedStyle]) => {
                    const variantData = referredVariants[scopedStyleId].data
                    // Scoped styles of stylable components working only with breakpoints
                    if (variantData.type === 'BreakpointRange') {
                        const {max, min} = variantData
                        acc.push({
                            ...getStyleWithMetadata(scopedStyle, {max, min}),
                            scopedStyleId
                        })
                    }
                    return acc
                },
                []
            )

            stylesWithMetadata.push(...scopedStylesWithMetadata)
        }

        const parsedStyleItems = parseStylableStyleItems(stylesWithMetadata, {
            selectorsMappings
        })

        const migratedStyles = migrateStyles({
            parsedStyleItems,
            newSkin,
            useCssColor,
            withBorderRadiusAsOneParam,
            postStyleMigrationCallback
        })

        migratedStyles.forEach(({styleItem, scopedStyleId, mobileStyleProperties}) => {
            if (scopedStyleId) {
                serializedComponent.scopedStyles![scopedStyleId] = styleItem
            } else {
                serializedComponent.style = styleItem

                if (mobileStyleProperties) {
                    if (!serializedComponent.scopedStyles) {
                        serializedComponent.scopedStyles = {}
                    }

                    serializedComponent.scopedStyles[MOBILE_VARIANT] = {
                        ...serializedComponent.style,
                        style: mobileStyleProperties
                    }
                }
            }
        })

        serializedComponent.skin = newSkin
    }
}

export default migrateStylableComponent
